import { Classes, Icon, IconSize } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import classNames from 'classnames';
import * as React from 'react';
import { Text } from 'vet-bones/components';
import { isEqual, isNotEqual } from 'vet-bones/utils';
import { SelectItem, createSelectItem } from 'vet-bones/utils/select';

import { Link } from 'src/components/Link';
import {
  getProjectMappingAttributeTestId,
  getProjectMappingLinkTestId,
} from 'src/constants/testIds';
import { useClickModelConceptAttributeLink } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useClickModelConceptAttributeLink';
import { useClickModelConceptClassLink } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useClickModelConceptClassLink';
import {
  Mapping,
  MappingField,
} from 'src/containers/ProjectDiagram/types/data';
import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import {
  useAddFieldValue,
  useChangeFieldValue,
  useRemoveFieldValue,
} from 'src/containers/ProjectMapping/hooks/attributes';
import {
  useMappingFromRoute,
  usePrevMappingFromRoute,
} from 'src/containers/ProjectMapping/hooks/useMappingFromRoute';
import { ProjectClassMappingAttributeFieldRow } from 'src/containers/ProjectMapping/ProjectClassMappingAttributeFieldRow';
import { isDraftDiagramSelector } from 'src/selectors/diagramSnapshot';
import { attributeDraftChangesSelector } from 'src/selectors/draftAttribute';
import { projectClassAttributesSelector } from 'src/selectors/projectClassAttributes';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

type ProjectClassMappingAttributeRowProps = {
  attributeIri: string;
  fieldValues: Array<string | null>;
  index: number;
};

const reduceAttributeFields =
  (attributeIri: string) => (acc: Set<string>, field: MappingField) => {
    if (field.targets.includes(attributeIri)) {
      acc.add(field.value);
    }
    return acc;
  };

export const ProjectClassMappingAttributeRow: React.VFC<
  ProjectClassMappingAttributeRowProps
> = ({ attributeIri, fieldValues, index }) => {
  const mapping = useMappingFromRoute() as Mapping & { to: string };
  const prevMapping = usePrevMappingFromRoute();
  const { projectId } = useRouterParams();
  const isDraftDiagram = useReduxSelector(isDraftDiagramSelector(projectId));
  const {
    addedDomains,
    isDatatypeModified,
    isDescriptionModified,
    isLabelModified,
  } = useReduxSelector(
    attributeDraftChangesSelector(projectId, attributeIri),
    isEqual
  );

  const attributeFields = Object.values(mapping?.fields || {}).reduce<
    Set<string>
  >(reduceAttributeFields(attributeIri), new Set());
  const prevAttributeFields = Object.values(prevMapping?.fields || {}).reduce<
    Set<string>
  >(reduceAttributeFields(attributeIri), new Set());

  const highlightAttribute =
    isDraftDiagram &&
    Boolean(
      addedDomains.size ||
        isDatatypeModified ||
        isDescriptionModified ||
        isLabelModified ||
        isNotEqual(attributeFields, prevAttributeFields)
    );

  const addFieldValue = useAddFieldValue();
  const changeFieldValue = useChangeFieldValue();
  const removeFieldValue = useRemoveFieldValue();

  const allFieldItems: SelectItem[] = Object.values(mapping.fields).map(
    (field) => ({ value: field.value, text: field.label })
  );

  const attributesByIri = useReduxSelector(
    projectClassAttributesSelector(
      projectId,
      getModelConceptClassValue(mapping.to)
    )
  );

  const labelsByIri = useReduxSelector(projectLabelsByIriSelector(projectId));

  const attribute = attributesByIri[attributeIri];

  const handleAddRow = (index: number) => {
    addFieldValue({ attributeIri, index: index + 1 });
  };

  const handleRemoveRow = (index: number, fieldId: string | null) => {
    removeFieldValue({ attributeIri, index, fieldId });
  };

  const handleSelectField =
    (oldFieldId: string | null, index: number) => (item: SelectItem | null) => {
      changeFieldValue({
        attributeIri,
        index,
        oldFieldId,
        newFieldId: item?.value || null,
      });
    };

  const handleClickClass = useClickModelConceptClassLink();
  const handleClickAttributeLink = useClickModelConceptAttributeLink();

  // possible to hit while changing IRI for attribute
  if (!attribute) {
    return null;
  }

  return (
    <>
      <div
        className={classNames(
          'sd-project-mapping-grid-cell',
          'sd-project-mapping-grid-cell--attribute',
          'sd-project-mapping-grid-cell--border-right',
          'sd-draft-highlight-candidate',
          {
            'sd-draft-highlight-candidate--highlighted': highlightAttribute,
            'sd-project-mapping-grid-cell--no-border': fieldValues.length !== 1,
          }
        )}
        data-testid={getProjectMappingAttributeTestId(attribute.value, index)}
      >
        <div className="sd-project-mapping-grid-cell-left">
          <Icon icon={IconNames.STOP} size={IconSize.LARGE} />
          <div className="sd-project-mapping-grid-cell-left-label">
            <Link
              onClick={() =>
                handleClickAttributeLink(
                  createSelectItem(attribute.value, attribute.value)
                )
              }
              testId={getProjectMappingLinkTestId(attribute.value)}
            >
              <Text ellipsize>
                {getLabelForIri(
                  labelsByIri,
                  attribute.value,
                  copy.components.projectDiagram.modelConceptAttribute.new
                )}
              </Text>
            </Link>
            {attribute.depth === 1 ? null : (
              <Text
                className={classNames(
                  'sd-project-mapping-grid-cell-left-label-subtext',
                  Classes.TEXT_SMALL
                )}
                ellipsize
              >
                {copy.components.projectMapping.attributes.inheritedFrom}
                &nbsp;
                {Array.from(attribute.sourceClassIris).map((classIri, idx) => (
                  <div key={classIri}>
                    {idx !== 0 ? <>,&nbsp;</> : null}
                    <Link
                      key={classIri}
                      onClick={() =>
                        handleClickClass(createSelectItem(classIri, classIri))
                      }
                      testId={getProjectMappingLinkTestId(classIri)}
                    >
                      {getLabelForIri(labelsByIri, classIri)}
                    </Link>
                  </div>
                ))}
              </Text>
            )}
          </div>
        </div>
        <div className="sd-project-mapping-grid-cell-right">
          <Text ellipsize>
            {Object.values(attribute.ranges)
              .map((range) =>
                getLabelForIri(
                  labelsByIri,
                  range.value,
                  copy.components.projectDiagram.modelConceptClass.new
                )
              )
              .join(', ')}
          </Text>
        </div>
      </div>

      {fieldValues.map((value, fieldIndex) => {
        const fieldItem = value
          ? allFieldItems.find((item) => item.value === value) || null
          : null;

        const highlighted =
          isDraftDiagram &&
          Boolean(
            prevAttributeFields.size
              ? !fieldItem || !prevAttributeFields.has(fieldItem.value)
              : fieldItem
          );

        return (
          <ProjectClassMappingAttributeFieldRow
            // no way around using index, since duplicate fields can exist
            // eslint-disable-next-line react/no-array-index-key
            key={`${value}-${fieldIndex}`}
            fieldItems={allFieldItems}
            highlighted={highlighted}
            index={fieldIndex}
            noBorder={fieldIndex !== fieldValues.length - 1}
            onAddRow={() => handleAddRow(fieldIndex)}
            onRemoveRow={
              fieldValues.length > 1
                ? () => handleRemoveRow(fieldIndex, value)
                : undefined
            }
            onSelectField={handleSelectField(value, fieldIndex)}
            selectedFieldItem={fieldItem}
            testId={`${attributeIri}-${value || ''}-${fieldIndex}`}
          />
        );
      })}
    </>
  );
};
